import {
  cloneElement,
  isValidElement,
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { JSX, ReactNode } from 'react';
import type { TakiConfig } from '@taki/runtime-config';
import {
  Button,
  cn,
  IconCog,
  IconColorSwatches,
  IconApplicationAdd,
  IconPlus,
  IconSearch,
  IconChatTwoBubblesOval,
  IconBellNotification,
  IconUser,
  ScreenLayout,
  SettingsActions,
  SettingsChoices,
  SettingsDoorProvider,
  SettingsEmpty,
  SettingsGroup,
  SettingsInput,
  SettingsMenu,
  SettingsPanel,
  SettingsRow,
  SettingsSection,
  SettingsTable,
  SkeletonText,
  Switch,
  useAccesPort,
  useDeclarationsPort,
  usePref,
} from '@taki/ui';
// [MENU DEV] Second bloc d'imports @taki/ui, servant UNIQUEMENT l'onglet DEV
// (l'icone de son entree de rail). Groupe a part pour partir d'un seul geste :
// fondu dans le bloc ci-dessus, il survivrait a la suppression sans que rien ne
// le signale (le typecheck ne traque pas les imports morts).
import { IconBrowserCode2 } from '@taki/ui';
import type { ScreenMenuItem } from '@taki/ui';
import {
  DOCK_APPS_MAX,
  DOCK_HOVER_LABEL_PREF_KEY,
  DOCK_ICONS,
  DOCK_MODE_LABELS,
  DOCK_MODES,
  iconRegistrySnapshot,
  isHoverLabelOn,
  loadIconRegistry,
  RegistryIcon,
  resolveDockApps,
  resolveDockIcon,
  resolveDockMode,
  libelleDeRaccourci,
  resolveDockShortcuts,
} from '../Dock.js';
import type { DockMode, DockShortcutPref, ResolvedDockShortcut } from '../Dock.js';
import { raccourciUtilisable } from '../raccourcis.js';
import type { ShellAppEntry } from '../types.js';
import { declarationCourante, jeuxDeLOrgDu, modeleDe } from '../studio/client/declaration.js';
// [MENU DEV] Bloc temporaire, suppression : packages/shell/src/dev/README.md
import {
  preserveHiddenDockApps,
  preserveHiddenDockShortcuts,
  useListedAppEntries,
} from '../appListing.js';
import { EnvironnementSettingsPanel } from './EnvironnementSettingsPanel.js';
import { MembresSettingsPanel } from './MembresSettingsPanel.js';
import { ConversationsSettingsPanel } from './ConversationsSettingsPanel.js';
import { NotificationsSettingsPanel } from './NotificationsSettingsPanel.js';
import { InstallationSettingsPanel } from './InstallationSettingsPanel.js';
import { CONVERSATIONS_MENU_ID, INSTALLATION_MENU_ID, NOTIFICATIONS_MENU_ID, cleDOnglet } from './ongletsParametres.js';
import { APP_MENU_PREFIX, portesDeReglages, type PorteDeReglages } from './reglagesDePorte.js';

/**
 * Hub Parametres du shell (route /settings), rendu dans le meme layout que
 * les apps (dock visible, surface pleine). Le rail est range en QUATRE
 * GROUPES, du plus proche au plus large (06/10/2026) :
 *   - Personnel : "Dock" (les reglages de mon poste) ;
 *   - Organisation : "Environnement" (nom, logo, couleur), "Utilisateurs" ;
 *   - Communication : "Conversations" (canaux, fenetre de chat, Lia ; l'ancien
 *     onglet "IA", dont la cle `ia` redirige), "Notifications" ;
 *   - Apps : un onglet PAR app installee : si l'app declare `loadSettings`,
 *     son ecran de reglages est monte paresseusement ; sinon, etat vide.
 * [MENU DEV] En dev local seulement, un onglet "DEV" en PIED de rail, separe
 * des reglages : voir `DevScreen` plus bas.
 *
 * Exception assumee : les entrees du menu portent une icone (usage reserve au
 * hub, cf. ScreenMenuItem ; les apps n'en mettent jamais).
 */

const menuAvance = (appId: string): string => `${APP_MENU_PREFIX}${appId}/avance`;
const OptionsAppSettingsPanel = lazy(() => import('./OptionsAppSettingsPanel.js'));

/** Icone du registre (size 22) reduite pour un item de menu du hub. */
function menuIconOf(entry: ShellAppEntry): ReactNode {
  return isValidElement(entry.icon)
    ? cloneElement(entry.icon as JSX.Element, { size: 15 })
    : entry.icon;
}

/**
 * Id de l'onglet fixe de l'environnement (`/settings?t=environnement`).
 * EXPORTE pour que le test de l'onglet (`tests/environnement.test.mjs`) cite la
 * constante au lieu de recopier la chaine : l'adresse est un contrat.
 */
export const ENVIRONNEMENT_MENU_ID = 'environnement';

/** Id de l'onglet fixe des utilisateurs (`/settings?t=membres`). */
const MEMBRES_MENU_ID = 'membres';

/*
 * LE GABARIT DES PARAMETRES, reference des briques du kit : seulement sur la
 * pile de dev locale, jamais en production. Meme couple litteral que l'onglet
 * DEV plus bas (`import.meta.env.DEV` + import() dynamique) : au build de
 * production la constante vaut `null`, l'entree et son morceau disparaissent.
 */
const ParametresTemplatePanel = import.meta.env.DEV
  ? lazy(() => import('./ParametresTemplatePanel.js'))
  : null;
const GABARIT_MENU_ID = 'gabarit-parametres';

/*
 * LES GROUPES DU RAIL (`ScreenMenuItem.groupe`, en-tete en petites capitales,
 * sans « + ») : ils disent a qui s'adresse un onglet avant qu'on l'ouvre.
 * L'ordre va du plus proche (mon poste) au plus large (les apps).
 */
const GROUPE_PERSONNEL = { id: 'personnel', label: 'Personnel' };
const GROUPE_ORGANISATION = { id: 'organisation', label: 'Organisation' };
const GROUPE_COMMUNICATION = { id: 'communication', label: 'Communication' };
const GROUPE_APPS = { id: 'apps', label: 'Apps' };

// [MENU DEV] L'onglet DEV n'existe qu'en dev local. Meme couple LITTERAL que
// partout ailleurs dans la greffe : drapeau `import.meta.env.DEV` + import()
// DYNAMIQUE. Au build de production la constante vaut `null`, la branche
// disparait, et avec elle le chunk de l'ecran : ni entree de rail, ni ecran, ni
// appel reseau chez un client. Ne JAMAIS remplacer `import.meta.env.DEV` par un
// helper importe, une variable intermediaire ou un optional chaining : c'est ce
// couple-la qui fait disparaitre la branche PUIS le chunk.
//
// IL A DEMENAGE DEPUIS LE PANNEAU HOME (chantier home-ui) : le rail du bureau
// est une surface CLIENT, l'outil de developpement n'y a pas sa place. Le hub
// Parametres est deja l'endroit ou l'on regle la machine, et son rail a un PIED
// (`placement: 'bottom'`, @taki/ui) : l'onglet s'y pose apres un espace
// flexible et un filet, sans se melanger aux reglages.
const DevScreen = import.meta.env.DEV ? lazy(() => import('../dev/DevScreen.js')) : null;

/** [MENU DEV] Id de l'onglet DEV (`/settings?t=dev`). */
const DEV_MENU_ID = 'dev';

export interface SettingsHubScreenProps {
  /**
   * Apps installees, liste COMPLETE, dans l'ordre de la liste de l'org.
   * Complete et non filtree : `DockSettingsPanel` ne fait pas qu'afficher cette
   * liste, il REECRIT les prefs du dock a partir d'elle.
   */
  appEntries: readonly ShellAppEntry[];
  /**
   * TOUTES les apps que ce bundle sait servir, installees ou non : l'onglet
   * « Apps installees » les propose. Absent (hub monte a la main, tests) : les
   * apps installees seules.
   */
  catalogue?: readonly ShellAppEntry[];
  /**
   * Config runtime : l'org et son branding, lus par l'onglet Utilisateurs et
   * par l'onglet Environnement (qui s'en sert de repli, sans port de branding).
   */
  config: TakiConfig;
  /**
   * L'ONGLET DEMANDE PAR L'ADRESSE (`/settings?t=membres`), quand l'hote
   * branche la route. Absent : l'onglet est un simple etat interne, comme
   * avant. Meme partage des roles que la route d'app : le hub ne connait ni le
   * routeur, ni le nom du parametre.
   */
  tab?: string;
  /** L'onglet a change : le shell l'ecrit dans l'adresse (en `replace`). */
  onTabChange?: (id: string) => void;
  /**
   * OUVRIR LE STUDIO D'UNE APP (`/studio/<appId>`). Le hub ne connait pas le
   * routeur, comme pour `onTabChange` : il nomme la cible, le shell navigue.
   * Absent (hub monte a la main, tests) : le bouton se grise et dit pourquoi.
   */
  onOuvrirStudio?: (appId: string) => void;
}

export function SettingsHubScreen({
  appEntries,
  catalogue,
  config,
  tab,
  onTabChange,
  onOuvrirStudio,
}: SettingsHubScreenProps): JSX.Element {
  /*
   * L'ADRESSE MENE, L'ETAT INTERNE SUIT, et rien ne se corrige apres coup.
   * Quand l'hote passe `tab`, c'est lui qui fait foi ; sans lui (hub monte a
   * la main, tests), l'etat local suffit. Un `useEffect` qui recopierait
   * l'adresse dans l'etat rendrait une frame sur le mauvais onglet.
   */
  const [tabLocal, setTabLocal] = useState('dock');
  const [appsDepliees, setAppsDepliees] = useState<Record<string, boolean>>({});
  const declarations = useDeclarationsPort();
  /* Une cle RENOMMEE (`ia`) ouvre sa remplacante, meme hub monte sans le
     routeur du shell (qui la traduit deja dans `validateSettingsSearch`). */
  const activeMenuId = cleDOnglet(tab ?? tabLocal);
  const selectMenu = (id: string): void => {
    if (id.startsWith(APP_MENU_PREFIX)) {
      const appId = id.slice(APP_MENU_PREFIX.length).split('/')[0]!;
      const parentId = APP_MENU_PREFIX + appId;
      const parentActif = activeMenuId === parentId || activeMenuId === menuAvance(appId);
      setAppsDepliees(previous => ({
        ...previous,
        [appId]: id === parentId && parentActif ? !(previous[appId] ?? true) : true,
      }));
    }
    setTabLocal(id);
    onTabChange?.(id);
  };

  // [MENU DEV] Le masquage ne porte QUE la barre d'onglets. `DockSettingsPanel`
  // garde la liste complete plus bas : il resout puis REECRIT `dock.apps` et
  // `dock.shortcuts` a partir de ce qu'il recoit, donc lui passer une liste
  // filtree effacerait pour de bon l'epinglage d'une app masquee. Le masquage
  // doit cacher, jamais detruire une autre preference.
  const menuAppEntries = useListedAppEntries(appEntries);

  const jeuxDeLOrg = useMemo(() => jeuxDeLOrgDu(declarations?.instances), [declarations?.instances]);
  const portesParApp = useMemo(() => new Map(menuAppEntries.map(entry => {
    const modele = modeleDe(entry);
    if (modele === null) return [entry.id, [] as PorteDeReglages[]] as const;
    try {
      /* TOUTES LES PORTES, celles hors navigation comprises (`reglagesDePorte.ts`). */
      return [entry.id, portesDeReglages(entry.id, declarationCourante(modele, declarations?.instances.get(entry.id) ?? null, jeuxDeLOrg))] as const;
    } catch { return [entry.id, [] as PorteDeReglages[]] as const; }
  })), [menuAppEntries, declarations?.instances, jeuxDeLOrg]);
  const appDemandee = menuAppEntries.find(entry => activeMenuId === APP_MENU_PREFIX + entry.id || activeMenuId.startsWith(`${APP_MENU_PREFIX}${entry.id}/`));
  const portesDemandees = appDemandee ? portesParApp.get(appDemandee.id) ?? [] : [];
  // L’ancien sous-onglet Avancé rejoint les réglages généraux de l’app.
  const menuDemande = appDemandee && activeMenuId === menuAvance(appDemandee.id)
    ? APP_MENU_PREFIX + appDemandee.id : activeMenuId;

  const menuItems = useMemo<ScreenMenuItem[]>(
    () => [
      { id: 'dock', label: 'Dock', icon: <IconCog size={15} />, groupe: GROUPE_PERSONNEL },
      ...(ParametresTemplatePanel != null
        ? [{ id: GABARIT_MENU_ID, label: 'Gabarit paramètres', icon: <IconColorSwatches size={15} />, groupe: GROUPE_PERSONNEL }]
        : []),
      /* L'onglet de l'environnement est FIXE lui aussi, et pour la meme raison
         que celui des utilisateurs : ferme, il explique pourquoi. Il se pose
         AVANT les utilisateurs parce qu'on regle d'abord la maison (son nom,
         ses couleurs), ensuite qui y entre. */
      {
        id: ENVIRONNEMENT_MENU_ID,
        label: 'Environnement',
        icon: <IconColorSwatches size={15} />,
        groupe: GROUPE_ORGANISATION,
      },
      /* L'onglet des utilisateurs est FIXE, et visible meme pour qui n'a pas
         le droit d'y toucher : le panneau explique alors pourquoi il est
         ferme (G16). Un onglet qui disparait ne s'explique pas. */
      { id: MEMBRES_MENU_ID, label: 'Utilisateurs', icon: <IconUser size={15} />, groupe: GROUPE_ORGANISATION },
      /* Les apps installees et l'environnement, lus dans la base de l'org.
         Fixe et visible de tous : qui n'a pas le droit voit les interrupteurs
         grises, avec leur raison (G16). */
      {
        id: INSTALLATION_MENU_ID,
        label: 'Apps installées',
        icon: <IconApplicationAdd size={15} />,
        groupe: GROUPE_ORGANISATION,
      },
      /* Les conversations (ex-« IA ») : les canaux, la fenetre de chat et Lia.
         Fixe, et chaque section y dit si elle vaut pour soi ou pour l'org. */
      {
        id: CONVERSATIONS_MENU_ID,
        label: 'Conversations',
        icon: <IconChatTwoBubblesOval size={15} />,
        groupe: GROUPE_COMMUNICATION,
      },
      /* Les notifications : chaque membre y regle ce qu'il recoit, owner et
         admin y ecrivent les regles. Fixe, pour la meme raison. */
      {
        id: NOTIFICATIONS_MENU_ID,
        label: 'Notifications',
        icon: <IconBellNotification size={15} />,
        groupe: GROUPE_COMMUNICATION,
      },
      ...menuAppEntries.flatMap((entry): ScreenMenuItem[] => {
        const portes = portesParApp.get(entry.id) ?? [];
        const deplie = appsDepliees[entry.id] ?? appDemandee?.id === entry.id;
        return [{
          id: APP_MENU_PREFIX + entry.id, label: entry.label,
          icon: menuIconOf(entry), groupe: GROUPE_APPS,
        }, ...(deplie ? portes.map(porte => ({
          id: porte.menuId, label: porte.label, niveau: 1, groupe: GROUPE_APPS,
        })) : [])];
      }),
      /* [MENU DEV] Entree temporaire, en PIED de rail. On teste
         `DevScreen != null` plutot que de reecrire le drapeau : une seule
         condition a maintenir, et l'entree ne peut pas exister sans son ecran.
         En production la constante vaut `null`, l'entree n'est jamais
         construite, et `/settings?t=dev` retombe donc sur Dock par le repli
         d'onglet inconnu, quelques lignes plus bas. */
      ...(DevScreen != null
        ? [
            {
              id: DEV_MENU_ID,
              label: 'DEV',
              icon: <IconBrowserCode2 size={15} />,
              placement: 'bottom' as const,
            },
          ]
        : []),
    ],
    [menuAppEntries, portesParApp, appsDepliees, appDemandee?.id],
  );

  // Un onglet peut disparaitre sous les pieds de l'utilisateur (app masquee
  // depuis le menu Home pose par-dessus le hub). Sans ce repli, `activeMenuId`
  // designerait un onglet absent : aucun onglet surligne, et la zone de contenu
  // annoncerait "Aucun reglage pour cette app" alors qu'aucune app n'est
  // choisie. Un repli calcule, jamais un useEffect qui corrige apres coup.
  const effectiveMenuId = menuItems.some(item => item.id === menuDemande)
    || portesDemandees.some(porte => porte.menuId === menuDemande)
    ? menuDemande : 'dock';

  const activeEntry = useMemo(
    () => menuAppEntries.find(entry => APP_MENU_PREFIX + entry.id === effectiveMenuId || effectiveMenuId.startsWith(`${APP_MENU_PREFIX}${entry.id}/`)),
    [menuAppEntries, effectiveMenuId],
  );

  const porteActive = activeEntry ? portesParApp.get(activeEntry.id)?.find(porte => porte.menuId === effectiveMenuId) ?? null : null;

  // Memoise par entree : recreer le composant lazy a chaque rendu
  // demonterait/remonterait l'ecran de reglages (perte d'etat), comme pour la
  // route d'app du shell.
  const LazySettings = useMemo(
    () => (activeEntry?.loadSettings != null ? lazy(activeEntry.loadSettings) : null),
    [activeEntry],
  );


  let zone5: ReactNode = null;
  let emptySettings = false;
  if (effectiveMenuId === 'dock') {
    /*
     * LE PANNEAU DOCK RECOIT LA LISTE COMPLETE, et ce n'est pas un oubli : il
     * REECRIT `dock.apps` et `dock.shortcuts` a partir de ce qu'il recoit. Il
     * filtre lui-meme ce qu'il AFFICHE, et preserve dans la pref ce qu'il
     * n'affiche pas. Lui donner une liste deja filtree effacerait l'epinglage
     * des apps filtrees au premier clic.
     */
    zone5 = <DockSettingsPanel appEntries={appEntries} />;
  } else if (effectiveMenuId === GABARIT_MENU_ID && ParametresTemplatePanel != null) {
    zone5 = <Suspense fallback={<SettingsLazyFallback title="Gabarit paramètres" />}><ParametresTemplatePanel /></Suspense>;
  } else if (effectiveMenuId === ENVIRONNEMENT_MENU_ID) {
    /* La config sert de REPLI au panneau (le nom et le logo de deploiement)
       quand aucun port de branding n'est branche : le hub ne sait pas lire le
       document, et n'a pas a le savoir. */
    zone5 = <EnvironnementSettingsPanel config={config} />;
  } else if (effectiveMenuId === MEMBRES_MENU_ID) {
    // Meme raison que le panneau Dock : la liste COMPLETE. Une app retiree de
    // la liste affichee reste installee, donc ses portes restent a ouvrir ou a
    // fermer pour les autres membres.
    zone5 = <MembresSettingsPanel appEntries={appEntries} config={config} />;
  } else if (effectiveMenuId === INSTALLATION_MENU_ID) {
    zone5 = <InstallationSettingsPanel catalogue={catalogue ?? appEntries} />;
  } else if (effectiveMenuId === CONVERSATIONS_MENU_ID) {
    // La liste COMPLETE : le panneau y cherche l'app Canaux pour monter ses
    // reglages, masquee ou non dans le rail.
    zone5 = <ConversationsSettingsPanel appEntries={appEntries} config={config} />;
  } else if (effectiveMenuId === NOTIFICATIONS_MENU_ID) {
    zone5 = <NotificationsSettingsPanel />;
    // [MENU DEV] Branche a retirer en entier, avec son Suspense.
  } else if (effectiveMenuId === DEV_MENU_ID && DevScreen != null) {
    /* LA LISTE COMPLETE, et c'est la raison d'etre de l'ecran : il est le seul
       endroit qui recoche une app masquee. Une liste filtree lui retirerait
       precisement les lignes qu'on vient y chercher. */
    zone5 = (
      <SettingsPanel>
        <Suspense
          fallback={
            <SettingsSection title="Branches et clones">
              <SkeletonText lines={3} />
            </SettingsSection>
          }
        >
          <DevScreen appEntries={appEntries} />
        </Suspense>
      </SettingsPanel>
    );
  } else if (activeEntry !== undefined && (LazySettings != null || activeEntry.declaration != null)) {
    zone5 = <ReglagesApp key={`${config.orgId}:${activeEntry.id}`} entry={activeEntry} orgId={config.orgId}
      reglages={LazySettings === null ? null : <LazySettings />}
      porte={porteActive}
      onOuvrirStudio={onOuvrirStudio} />;
  } else {
    // App sans `loadSettings` : etat vide standard du gabarit.
    emptySettings = true;
  }

  return (
    <ScreenLayout
      appName="Paramètres"
      appIcon={<IconCog size={18} />}
      menuItems={menuItems}
      menuHierarchyStyle="line"
      activeMenuId={effectiveMenuId}
      onMenuSelect={selectMenu}
      title={activeEntry?.label ?? "Paramètres"}
      titleContent={activeEntry ? <>
        <span>{activeEntry.label}</span>{' '}
        <span className="ml-3 text-sm font-normal tracking-normal text-ink-500">{porteActive ? `Réglages porte ${porteActive.label}` : 'Réglages généraux'}</span>
      </> : undefined}
      empty={emptySettings}
      emptyTitle="Aucun reglage pour cette app"
    >
      {zone5}
    </ScreenLayout>
  );
}

function ReglagesApp({ entry, orgId, reglages, porte, onOuvrirStudio }: {
  entry: ShellAppEntry;
  orgId: string;
  reglages: ReactNode;
  porte: PorteDeReglages | null;
  onOuvrirStudio?: (appId: string) => void;
}): JSX.Element {
  /* UNE PORTE : Mon affichage (pose par l'app, `ReglagesDeclares`), puis ses
     Étiquettes et listes. L'APP : ses reglages generaux, puis le Studio. Tout
     vient du socle et de la declaration, aucune app n'ecrit cet ecran. */
  return <SettingsDoorProvider value={porte}><SettingsPanel>
    <Suspense fallback={<SettingsSection title={entry.label}><SkeletonText lines={3} /></SettingsSection>}>
      {reglages}
      {porte !== null && entry.declaration ? <SettingsSection title="Étiquettes et listes"
        contextLabel={orgId === 'master' ? 'Modèle · administrateurs' : 'Organisation · administrateurs'}>
        <OptionsAppSettingsPanel entry={entry} orgId={orgId} />
      </SettingsSection> : null}
      {porte === null && entry.declaration ? <SettingsSection title="Studio" contextLabel="Administration">
        <SettingsRow label="Structure">
          <Button variant="secondary" size="sm" disabled={!onOuvrirStudio} title={!onOuvrirStudio ? 'Studio indisponible ici.' : undefined} onClick={() => onOuvrirStudio?.(entry.id)}>Ouvrir le Studio</Button>
        </SettingsRow>
      </SettingsSection> : null}
    </Suspense>
  </SettingsPanel></SettingsDoorProvider>;
}

/**
 * Squelette d'attente pendant le chargement paresseux d'un `settings.tsx` :
 * l'en-tete d'une section + quelques lignes de texte, dans le MEME cadre que
 * les vrais panneaux (`SettingsPanel`, kit v2.2) : a l'arrivee du chunk, le
 * titre de section ne bouge pas d'un rem.
 */
function SettingsLazyFallback({ title }: { title: string }): JSX.Element {
  return (
    <SettingsPanel>
      <SettingsSection title={title}>
        <SkeletonText lines={3} />
      </SettingsSection>
    </SettingsPanel>
  );
}

/* -------------------------------------------------------------------------- */
/* Onglet Dock : apps mises en avant + raccourcis d'action                     */
/* -------------------------------------------------------------------------- */

/** Action declaree, aplatie avec son app (options du select d'ajout). */
interface FlatAction {
  appId: string;
  appLabel: string;
  actionId: string;
  actionLabel: string;
  iconName: string;
}

/**
 * Reglages du dock, en trois sections : Affichage (`dock.mode`,
 * `dock.hoverLabel`), Apps du dock (`dock.apps`, plafond 6, ordre a la
 * poignee) et Raccourcis (`dock.shortcuts`).
 */
function DockSettingsPanel({
  appEntries,
}: {
  appEntries: readonly ShellAppEntry[];
}): JSX.Element {
  /*
   * DEUX LISTES, ET C'EST TOUT LE SUJET DE CE PANNEAU.
   *
   * `listees` = ce qu'on AFFICHE (deprecated exclues, masquage DEV applique).
   * `appEntries` = ce qui existe, et c'est sur lui que les preferences sont
   * ECRITES. Confondre les deux efface l'epinglage d'une app masquee des le
   * premier clic, et le masquage doit rester cosmetique et reversible.
   */
  const listees = useListedAppEntries(appEntries);
  const acces = useAccesPort();
  const listedIds = useMemo(() => new Set(listees.map((e) => e.id)), [listees]);
  const entriesById = useMemo(
    () => new Map(listees.map((entry) => [entry.id, entry])),
    [listees],
  );

  /* ---- (0) Comportement du rail ---- */

  // Lecture volontairement en `unknown` : la pref vient du localStorage, c'est
  // `resolveDockMode` (pure, jamais fatale) qui la rend sure.
  const [modePref, setModePref] = usePref<unknown>('dock.mode', null);
  const mode = resolveDockMode(modePref);

  /*
   * L'ETIQUETTE AU SURVOL, et pourquoi sa pref se lit ici comme celle du mode :
   * en `unknown`, rendue sure par une fonction pure (`isHoverLabelOn`).
   *
   * ON ECRIT LA DEVIATION, JAMAIS L'ETAT NORMAL : l'etiquette est ETEINTE par
   * defaut, donc allumer ecrit `true` et eteindre EFFACE la pref (`null`).
   * Ecrire `false` fabriquerait un doublon de l'etat par defaut, impossible a
   * distinguer d'un poste neuf a la relecture.
   */
  const [hoverLabelPref, setHoverLabelPref] = usePref<unknown>(DOCK_HOVER_LABEL_PREF_KEY, null);
  const hoverLabelOn = isHoverLabelOn(hoverLabelPref);

  /* ---- (a) Apps mises en avant ---- */

  const [appsPref, setAppsPref] = usePref<string[] | null>('dock.apps', null);
  const selectedIds = useMemo(
    () => resolveDockApps(appsPref, listees).map((entry) => entry.id),
    [appsPref, listees],
  );
  const selectedSet = new Set(selectedIds);

  /*
   * L'ORDRE SE DERIVE A CHAQUE RENDU, il n'est plus un `useState` a
   * initialiseur paresseux.
   *
   * POURQUOI : la liste affichee RETRECIT apres le montage. L'etat deprecated
   * demarre a `loading` (il n'exclut personne) puis passe a `known` une
   * microtache plus tard. Un ordre fige au montage garderait des ids devenus
   * absents, et la boucle de rendu produirait des lignes vides.
   */
  const [ordreLocal, setOrdreLocal] = useState<string[] | null>(null);
  const order = useMemo(() => {
    const selected = resolveDockApps(appsPref, listees).map((entry) => entry.id);
    const reste = listees.map((entry) => entry.id).filter((id) => !selected.includes(id));
    const naturel = [...selected, ...reste];
    if (ordreLocal === null) return naturel;
    /* Un ordre choisi a la main survit, mais seulement sur ce qui est encore
       affiche : le reste revient a sa place naturelle. */
    const connus = new Set(naturel);
    const garde = ordreLocal.filter((id) => connus.has(id));
    return [...garde, ...naturel.filter((id) => !garde.includes(id))];
  }, [appsPref, listees, ordreLocal]);
  const setOrder = setOrdreLocal;

  function writeSelection(nextOrder: string[], nextSelected: Set<string>): void {
    setAppsPref(preserveHiddenDockApps(appsPref, nextOrder, nextSelected, listedIds));
  }

  function toggleApp(id: string): void {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else {
      if (next.size >= DOCK_APPS_MAX) return;
      next.add(id);
    }
    writeSelection(order, next);
  }


  /* ---- (b) Raccourcis d'action ---- */

  const [shortcutsPref, setShortcutsPref] = usePref<DockShortcutPref[] | null>(
    'dock.shortcuts',
    null,
  );
  const shortcuts = useMemo(
    () => resolveDockShortcuts(shortcutsPref, listees),
    [shortcutsPref, listees],
  );

  /*
   * LE PIEGE JUMEAU DE `dock.apps`, et le plus discret : `resolveDockShortcuts`
   * laisse tomber les raccourcis dont l'app n'est pas dans la liste recue, et
   * cette fonction reecrit la pref a partir de ce qui reste. Trois gestes la
   * declenchent (deplacer, retirer, ajouter), pas seulement le retrait.
   */
  function writeShortcuts(next: readonly ResolvedDockShortcut[]): void {
    const propres = next.map(({ appId, actionId, iconName }) => ({ appId, actionId, iconName }));
    setShortcutsPref(preserveHiddenDockShortcuts(shortcutsPref, propres, listedIds));
  }

  const cleDeRaccourci = (shortcut: ResolvedDockShortcut): string => `${shortcut.appId}:${shortcut.actionId}`;
  const raccourcisParCle = new Map(shortcuts.map((shortcut) => [cleDeRaccourci(shortcut), shortcut]));
  const libelleDuRaccourci = (cle: string): string => {
    const shortcut = raccourcisParCle.get(cle);
    return shortcut === undefined ? cle : libelleDeRaccourci(shortcut.appLabel, shortcut.actionLabel);
  };

  // Les actions PROPOSEES a l'ajout viennent des apps AFFICHEES : proposer un
  // raccourci vers une app morte serait offrir un geste qui ne mene nulle part.
  // Celles de chaque PORTE en font partie (`raccourcis.ts`) : toute app qui
  // declare une creation la propose ici, sans rien ecrire de plus. Une action
  // deja epinglee n'est plus proposee : deux fois le meme raccourci dans le
  // dock ne donne rien de plus.
  const allActions = useMemo<FlatAction[]>(() => {
    const epingles = new Set(shortcuts.map((s) => `${s.appId}:${s.actionId}`));
    return listees.flatMap((entry) =>
      (entry.actions ?? [])
        .filter((action) => !epingles.has(`${entry.id}:${action.id}`))
        /* Une creation dans une porte ou ce membre n'ecrit pas n'est pas
           proposee : le dock ne la montrerait pas (`raccourciUtilisable`). */
        .filter((action) => raccourciUtilisable(action, acces))
        .map((action) => ({
          appId: entry.id,
          appLabel: entry.label,
          actionId: action.id,
          actionLabel: action.label,
          iconName: action.iconName,
        })),
    );
  }, [listees, shortcuts, acces]);

  // Bloc d'ajout : index (stringifie) de l'action choisie + icone choisie
  // (nom PascalCase de DOCK_ICONS ou clef kebab du registre complet).
  const [pickedAction, setPickedAction] = useState('');
  const [pickedIcon, setPickedIcon] = useState('IconModule');

  function addShortcut(): void {
    const item = allActions[Number(pickedAction)];
    if (item == null) return;
    writeShortcuts([
      ...shortcuts,
      {
        appId: item.appId,
        appLabel: item.appLabel,
        actionId: item.actionId,
        actionLabel: item.actionLabel,
        iconName: pickedIcon,
      },
    ]);
    setPickedAction('');
  }

  /* ---- Registre complet des icones (picker "Toutes les icônes") ---- */

  const [allIconsOpen, setAllIconsOpen] = useState(false);
  const [iconQuery, setIconQuery] = useState('');
  // Le registre (~centaines de KB) n'arrive QUE par import() dynamique : a
  // l'ouverture du picker complet, ou si un raccourci configure porte deja
  // une clef kebab (pour rendre son icone dans la liste).
  const [iconRegistry, setIconRegistry] = useState(iconRegistrySnapshot);
  const needsIconRegistry =
    allIconsOpen || shortcuts.some((shortcut) => DOCK_ICONS[shortcut.iconName] == null);
  useEffect(() => {
    if (!needsIconRegistry || iconRegistry != null) return;
    let cancelled = false;
    void loadIconRegistry().then((registry) => {
      if (!cancelled) setIconRegistry(registry);
    });
    return () => {
      cancelled = true;
    };
  }, [needsIconRegistry, iconRegistry]);

  const filteredIconNames = useMemo(() => {
    if (iconRegistry == null) return [];
    const query = iconQuery.trim().toLowerCase();
    const names = Object.keys(iconRegistry);
    return query === '' ? names : names.filter((name) => name.includes(query));
  }, [iconRegistry, iconQuery]);

  const actionChoisie = allActions[Number(pickedAction)];

  return (
    <SettingsPanel>
      <SettingsSection title="Affichage">
        <SettingsRow label="Ouverture du dock">
          <SettingsMenu
            label="Ouverture du dock"
            value={mode}
            onChange={(valeur) => setModePref(resolveDockMode(valeur))}
            options={DOCK_MODES.map((valeur: DockMode) => ({
              value: valeur,
              label: valeur === 'legacy' ? 'Au survol' : DOCK_MODE_LABELS[valeur],
            }))}
          />
        </SettingsRow>
        {/* TOUJOURS RENDUE, verrouillee hors du mode ferme : un reglage qui
            disparait se cherche, et la raison enseigne la regle. */}
        <SettingsRow label="Noms au survol">
          <Switch
            checked={hoverLabelOn}
            onChange={(next) => setHoverLabelPref(next ? true : null)}
            locked={mode !== 'closed'}
            lockedReason="Dock fermé uniquement"
            aria-label="Afficher le nom de l'app au survol quand le dock est ferme"
          />
        </SettingsRow>
      </SettingsSection>

      <SettingsSection title="Apps du dock" contextLabel={`${selectedSet.size} / ${DOCK_APPS_MAX} épinglées`}>
        <SettingsTable
          label="Apps du dock"
          columns={[{ label: 'App' }, { label: 'Dans le dock', fin: true, largeur: 'w-28' }]}
          onReorder={(next) => {
            setOrder(next);
            writeSelection(next, selectedSet);
          }}
          nomDe={(id) => entriesById.get(id)?.label ?? id}
          rows={order.flatMap((id) => {
            const entry = entriesById.get(id);
            if (entry == null) return [];
            const checked = selectedSet.has(id);
            const capped = !checked && selectedSet.size >= DOCK_APPS_MAX;
            return [{
              id,
              attenue: !checked,
              cells: [
                <span key="app" className="flex items-center gap-2.5">
                  <span className="flex w-5 shrink-0 items-center justify-center text-ink-600">
                    {isValidElement(entry.icon) ? cloneElement(entry.icon as JSX.Element, { size: 17 }) : entry.icon}
                  </span>
                  <span className={cn('min-w-0 truncate', checked && 'font-medium text-ink-900')}>{entry.label}</span>
                </span>,
                <Switch
                  key="dock"
                  checked={checked}
                  locked={capped}
                  lockedReason={`Limite de ${DOCK_APPS_MAX} apps`}
                  onChange={() => toggleApp(id)}
                  aria-label={`Afficher ${entry.label} dans le dock`}
                />,
              ],
            }];
          })}
        />
      </SettingsSection>

      <SettingsSection title="Raccourcis">
        {shortcuts.length > 0 ? (
          <SettingsTable
            label="Raccourcis"
            columns={[{ label: 'Raccourci' }]}
            onReorder={(cles) => writeShortcuts(cles.flatMap((cle) => raccourcisParCle.get(cle) ?? []))}
            onRemove={(cle) => writeShortcuts(shortcuts.filter((shortcut) => cleDeRaccourci(shortcut) !== cle))}
            nomDe={libelleDuRaccourci}
            rows={shortcuts.map((shortcut) => {
              const Icon = resolveDockIcon(shortcut.iconName, iconRegistry);
              return {
                id: cleDeRaccourci(shortcut),
                cells: [
                  <span key="raccourci" className="flex items-center gap-2.5">
                    <span className="flex w-5 shrink-0 items-center justify-center text-ink-600"><Icon size={17} /></span>
                    <span className="min-w-0 truncate font-medium text-ink-900">{libelleDuRaccourci(cleDeRaccourci(shortcut))}</span>
                  </span>,
                ],
              };
            })}
          />
        ) : (
          <SettingsEmpty>Aucun raccourci.</SettingsEmpty>
        )}

        {allActions.length > 0 ? (
          <SettingsGroup title="Ajouter un raccourci">
            <SettingsRow label="Action">
              <SettingsMenu
                label="Action à épingler"
                placeholder="Choisir une action"
                value={pickedAction}
                /* L'APP EN SECOND MOT : une app a une creation par porte, le nom
                   de l'action seul ne dirait pas laquelle. */
                options={allActions.map((item, index) => ({
                  value: String(index),
                  label: item.actionLabel,
                  detail: item.appLabel,
                }))}
                onChange={(valeur) => {
                  setPickedAction(valeur);
                  /* L'ICONE SUIT L'ACTION CHOISIE (le « + » d'une creation). */
                  const choisie = allActions[Number(valeur)];
                  if (choisie !== undefined) setPickedIcon(choisie.iconName);
                }}
              />
            </SettingsRow>
            <SettingsRow label="Icône">
              <SettingsChoices
                label="Icône du raccourci"
                value={pickedIcon}
                onChange={setPickedIcon}
                options={Object.entries(DOCK_ICONS).map(([name, Icon]) => ({ value: name, label: name, content: <Icon size={17} /> }))}
              />
              {!allIconsOpen ? (
                <Button variant="secondary" size="sm" onClick={() => setAllIconsOpen(true)}>Toutes les icônes</Button>
              ) : (
                <>
                  <SettingsInput
                    leftIcon={<IconSearch size={16} />}
                    value={iconQuery}
                    onChange={(event) => setIconQuery(event.target.value)}
                    placeholder="Rechercher une icône"
                    aria-label="Rechercher une icône"
                  />
                  {iconRegistry == null ? (
                    <SettingsEmpty>Chargement des icônes…</SettingsEmpty>
                  ) : filteredIconNames.length === 0 ? (
                    <SettingsEmpty>Aucune icône trouvée.</SettingsEmpty>
                  ) : (
                    <SettingsChoices
                      defile
                      label="Toutes les icônes"
                      value={pickedIcon}
                      onChange={setPickedIcon}
                      options={filteredIconNames.flatMap((name) => {
                        const inner = iconRegistry[name];
                        return inner == null ? [] : [{ value: name, label: name, content: <RegistryIcon inner={inner} size={17} /> }];
                      })}
                    />
                  )}
                </>
              )}
            </SettingsRow>
            <SettingsActions>
              <Button variant="secondary" size="sm" disabled={actionChoisie === undefined} onClick={addShortcut}>
                <IconPlus size={15} />
                Ajouter
              </Button>
            </SettingsActions>
          </SettingsGroup>
        ) : (
          <SettingsEmpty>{shortcuts.length > 0 ? 'Tout est épinglé.' : 'Aucune action disponible.'}</SettingsEmpty>
        )}
      </SettingsSection>
    </SettingsPanel>
  );
}
